"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";
// Straight from next-themes, not @ergon-ai/ui/theme: that module also
// exports ThemeToggle, which drags Base UI's dropdown and lucide into
// the client bundle of a route that renders neither.
import { ThemeProvider } from "next-themes";

import "@ergon-ai/ui/styles";

/**
 * Root error boundary for the App Router. It replaces the root layout, so it
 * renders its own <html>/<body> and nothing the layout provides reaches it:
 * no app context, no i18n, no shared component. ThemeProvider is the exception.
 *
 * This boundary swallows the error: React logs it, window.onerror never fires,
 * so the SDK's global handler never sees it. Report it from here or nowhere.
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="en" suppressHydrationWarning>
      <body
        suppressHydrationWarning
        style={{
          margin: 0,
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          color: "var(--foreground, #0f172a)",
          background: "var(--background, #f8fafc)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "1.5rem",
        }}
      >
        <title>Something went wrong · Earlyup</title>
        {/* The only provider allowed here: it reads the browser, not the
            layout. The colours above stay `var(--token, literal)` so a
            stylesheet that never loads gives the light page, not a blank one. */}
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <main style={{ maxWidth: 480, textAlign: "center" }}>
            <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
              Something went wrong
            </h1>
            <p
              style={{
                fontSize: "1rem",
                lineHeight: 1.5,
                color: "var(--muted-foreground, #475569)",
              }}
            >
              An unexpected error occurred. Our team has been notified and is
              looking into it. Please try again in a few moments.
            </p>
            {error.digest ? (
              <p
                style={{
                  fontSize: "0.75rem",
                  marginTop: "1.5rem",
                  color: "var(--muted-foreground, #94a3b8)",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                Reference: {error.digest}
              </p>
            ) : null}
          </main>
        </ThemeProvider>
      </body>
    </html>
  );
}
